import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchDataQuality } from '@/api';
import { LoadingPage } from '@/components/common/Loading';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Activity, AlertTriangle, Clock, Database, Layers, Shield, BarChart3, Wifi, ExternalLink, Zap } from 'lucide-react';
import { toast } from 'sonner';

export default function DataQualityPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDataQuality()
      .then(res => { setData(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <LoadingPage />;
  if (!data) return <div className="text-slate-500 text-center py-12">Failed to load data quality metrics</div>;

  const healthScore = calculateHealth(data);

  return (
    <div data-testid="data-quality-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight uppercase text-slate-50" style={{ fontFamily: 'var(--font-heading)' }}>
            <Activity className="w-5 h-5 inline mr-2 text-cyan-400" />
            Data Quality
          </h1>
        </div>
        <HealthBadge score={healthScore} />
      </div>

      {/* Health Score Bar */}
      <div className="mb-6 p-4 bg-slate-900/50 border border-slate-800 rounded-xl">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-slate-400">Overall Health</span>
          <span className={`text-sm font-bold ${healthScore >= 80 ? 'text-emerald-400' : healthScore >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
            {healthScore}%
          </span>
        </div>
        <div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden">
          <div className={`h-full rounded-full transition-all ${healthScore >= 80 ? 'bg-emerald-500' : healthScore >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
            style={{ width: `${healthScore}%` }} />
        </div>
      </div>

      {/* Metric Cards Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <MetricCard
          icon={Database} label="Total Matches" value={data.totals.matches}
          sub={`${data.matches.prematch} prematch`} color="blue" />
        <MetricCard
          icon={BarChart3} label="Active Odds" value={data.odds.total_active}
          sub={data.odds.stale_count > 0 ? `${data.odds.stale_count} stale` : 'All fresh'} color="emerald"
          alert={data.odds.stale_pct > 20} />
        <MetricCard
          icon={Layers} label="Value Bets" value={data.totals.value_bets}
          sub={`${data.totals.surebets} surebets`} color="amber" />
        <MetricCard
          icon={Shield} label="Bookmakers" value={data.coverage.bookmakers}
          sub={`${data.coverage.markets_active}/${data.coverage.markets_total} markets`} color="purple" />
      </div>

      {/* Issue Cards with Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
        <IssueCard
          icon={AlertTriangle}
          title="Matches Without Odds"
          value={data.matches.without_odds}
          total={data.matches.prematch}
          pct={data.matches.without_odds_pct}
          severity={data.matches.without_odds_pct > 30 ? 'critical' : data.matches.without_odds_pct > 10 ? 'warning' : 'ok'}
          actions={
            <div className="flex flex-wrap gap-2 mt-3">
              <Button size="sm" variant="outline" className="text-xs border-slate-700 text-slate-300 hover:text-amber-400"
                onClick={async () => {
                  try {
                    const API = process.env.REACT_APP_BACKEND_URL || '';
                    const token = localStorage.getItem('admin_token');
                    await fetch(`${API}/api/admin/noindex-zero-odds`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
                    toast.success('Odds-less matches excluded from sitemap');
                  } catch { toast.error('Failed'); }
                }}
                data-testid="action-exclude-sitemap">
                <Zap className="w-3 h-3 mr-1" />Exclude from Sitemap
              </Button>
              <Button size="sm" variant="outline" className="text-xs border-slate-700 text-slate-300 hover:text-emerald-400"
                onClick={async () => {
                  try {
                    const API = process.env.REACT_APP_BACKEND_URL || '';
                    const token = localStorage.getItem('admin_token');
                    toast.info('Triggering odds sync...');
                    const res = await fetch(`${API}/api/admin/manual-sync`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
                    const d = await res.json();
                    toast.success(`Sync started: ${d.status || 'ok'}`);
                  } catch { toast.error('Sync failed'); }
                }}
                data-testid="action-trigger-sync">
                <BarChart3 className="w-3 h-3 mr-1" />Trigger Odds Sync
              </Button>
            </div>
          }
        />
        <IssueCard
          icon={Clock}
          title="Stale Odds (>2h)"
          value={data.odds.stale_count}
          total={data.odds.total_active}
          pct={data.odds.stale_pct}
          severity={data.odds.stale_pct > 30 ? 'critical' : data.odds.stale_pct > 10 ? 'warning' : 'ok'}
        />
        <IssueCard
          icon={Layers}
          title="Pending Mappings"
          value={data.mappings.pending}
          total={data.mappings.total}
          pct={data.mappings.total > 0 ? Math.round(data.mappings.pending / data.mappings.total * 100) : 0}
          severity={data.mappings.pending > 100 ? 'critical' : data.mappings.pending > 20 ? 'warning' : 'ok'}
          actions={
            <Link to="/admin/mapping" className="inline-flex items-center gap-1 mt-3 text-xs text-amber-400 hover:text-amber-300">
              <ExternalLink className="w-3 h-3" />Go to Mapping Queue
            </Link>
          }
        />
        <IssueCard
          icon={Wifi}
          title="Provider Errors (24h)"
          value={data.providers.errors_24h}
          total={null}
          pct={null}
          severity={data.providers.errors_24h > 10 ? 'critical' : data.providers.errors_24h > 0 ? 'warning' : 'ok'}
        />
      </div>

      {/* Health Score Breakdown */}
      <div className="p-4 bg-slate-900/50 border border-slate-800 rounded-xl mb-6" data-testid="health-breakdown">
        <h3 className="text-sm font-medium text-slate-300 mb-3">Health Score Breakdown</h3>
        <div className="space-y-2">
          <BreakdownRow label="Odds Coverage" weight="30%" score={data.matches.without_odds_pct > 30 ? -25 : data.matches.without_odds_pct > 10 ? -10 : 0} />
          <BreakdownRow label="Odds Freshness" weight="25%" score={data.odds.stale_pct > 30 ? -25 : data.odds.stale_pct > 10 ? -10 : 0} />
          <BreakdownRow label="Mapping Completeness" weight="20%" score={data.mappings.pending > 100 ? -15 : data.mappings.pending > 20 ? -5 : 0} />
          <BreakdownRow label="Provider Stability" weight="25%" score={data.providers.errors_24h > 10 ? -20 : data.providers.errors_24h > 0 ? -5 : 0} />
        </div>
      </div>

      {/* Last Sync Info */}
      {data.providers.last_sync && (
        <div className="p-4 bg-slate-900/50 border border-slate-800 rounded-xl" data-testid="last-sync-info">
          <h3 className="text-sm font-medium text-slate-300 mb-2">Last Sync</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
            <div>
              <span className="text-slate-500">Started</span>
              <p className="text-slate-300 mt-0.5">{data.providers.last_sync.started_at ? new Date(data.providers.last_sync.started_at).toLocaleString() : '-'}</p>
            </div>
            <div>
              <span className="text-slate-500">Status</span>
              <div className="mt-0.5">
                <Badge className={data.providers.last_sync.status === 'success' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-xs' : 'bg-red-500/10 text-red-400 border-red-500/20 text-xs'}>
                  {data.providers.last_sync.status || 'unknown'}
                </Badge>
              </div>
            </div>
            <div>
              <span className="text-slate-500">Matches Fetched</span>
              <p className="text-slate-300 mt-0.5 tabular-nums">{data.providers.last_sync.matches_fetched ?? '-'}</p>
            </div>
            <div>
              <span className="text-slate-500">Odds Fetched</span>
              <p className="text-slate-300 mt-0.5 tabular-nums">{data.providers.last_sync.odds_fetched ?? '-'}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function calculateHealth(data) {
  let score = 100;
  // Deduct for matches without odds
  if (data.matches.without_odds_pct > 30) score -= 25;
  else if (data.matches.without_odds_pct > 10) score -= 10;
  // Deduct for stale odds
  if (data.odds.stale_pct > 30) score -= 25;
  else if (data.odds.stale_pct > 10) score -= 10;
  // Deduct for pending mappings
  if (data.mappings.pending > 100) score -= 15;
  else if (data.mappings.pending > 20) score -= 5;
  // Deduct for provider errors
  if (data.providers.errors_24h > 10) score -= 20;
  else if (data.providers.errors_24h > 0) score -= 5;
  return Math.max(0, Math.min(100, score));
}

function HealthBadge({ score }) {
  const color = score >= 80 ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' :
                score >= 50 ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' :
                'bg-red-500/10 text-red-400 border-red-500/20';
  const label = score >= 80 ? 'Healthy' : score >= 50 ? 'Warning' : 'Critical';
  return (
    <Badge className={`${color} text-sm px-3 py-1`} data-testid="health-badge">
      {label}: {score}%
    </Badge>
  );
}

function MetricCard({ icon: Icon, label, value, sub, color, alert }) {
  const colors = {
    blue: 'text-blue-400 bg-blue-500/10 border-blue-500/20',
    emerald: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
    amber: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
    purple: 'text-purple-400 bg-purple-500/10 border-purple-500/20',
  };
  return (
    <div className={`rounded-xl border p-4 ${alert ? 'border-red-500/30 bg-red-500/5' : colors[color]}`}>
      <Icon className="w-5 h-5 mb-2 opacity-60" />
      <p className="text-2xl font-bold tabular-nums">{typeof value === 'number' ? value.toLocaleString() : value}</p>
      <p className="text-xs opacity-60 mt-0.5">{label}</p>
      {sub && <p className="text-[10px] opacity-40 mt-0.5">{sub}</p>}
    </div>
  );
}

function IssueCard({ icon: Icon, title, value, total, pct, severity, actions }) {
  const sevColors = {
    ok: 'border-emerald-500/20 bg-emerald-500/5',
    warning: 'border-amber-500/20 bg-amber-500/5',
    critical: 'border-red-500/20 bg-red-500/5',
  };
  const sevText = { ok: 'text-emerald-400', warning: 'text-amber-400', critical: 'text-red-400' };
  return (
    <div className={`rounded-xl border p-4 ${sevColors[severity]}`} data-testid={`issue-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${sevText[severity]}`} />
        <span className="text-sm text-slate-300 font-medium">{title}</span>
      </div>
      <div className="flex items-end gap-2">
        <span className={`text-2xl font-bold tabular-nums ${sevText[severity]}`}>{value}</span>
        {total !== null && <span className="text-xs text-slate-500 mb-0.5">/ {total}</span>}
        {pct !== null && <Badge className={`ml-auto text-xs ${sevColors[severity]} ${sevText[severity]}`}>{pct}%</Badge>}
      </div>
      {actions}
    </div>
  );
}

function BreakdownRow({ label, weight, score }) {
  const color = score === 0 ? 'text-emerald-400' : score > -15 ? 'text-amber-400' : 'text-red-400';
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-slate-400">{label} <span className="text-slate-600">({weight})</span></span>
      <span className={`font-bold tabular-nums ${color}`}>{score === 0 ? 'OK' : `${score}`}</span>
    </div>
  );
}
